import React, { useContext, useEffect, useState } from 'react'
import { Badge } from '@entur/layout'
import { space } from '@entur/tokens'

import { AddIcon } from '@entur/icons'
import { BannerAlertBox } from '@entur/alert'
import { Loader } from '@entur/loader'
import { FamilyMember, FetchState } from '../../../api-clients/api-types'
import { useEmployee } from '../../../contexts/employee-context'
import { CardActionChip, TicketRightCardBase } from './ticket-right-card-base'
import { useLowIncome } from '../../../hooks/employee-yearly-data-hooks'
import { useGenerateTicketRights } from '../../../hooks/use-ticket-rights'
import { RuleElement } from '../rule-element'
import { TicketRightWithDraft } from '../../../types'
import { ChangedTicketRightDraft } from '../drafts/changed-ticket-right-draft'
import { ReadOnlyContext } from '../../../contexts/read-only-context'
import { AddNewCouponsModal } from '../../family-member/add-new-coupons'
import { useApi } from '../../../api-clients/client-hooks'
import { getCouponsStatus, patchCouponsLimit } from '../../../api-clients/eba-clients'
import { useClientInfo } from '../../../contexts/client-info-context'

type FamilyMemberTicketRightCardProps = {
  ticketRightWithDraft: TicketRightWithDraft
  familyMember: FamilyMember
}
export const FamilyMemberTicketRightCard: React.FC<FamilyMemberTicketRightCardProps> = ({
  ticketRightWithDraft,
  familyMember,
}) => {
  const { ticketRight, draft } = ticketRightWithDraft
  const { employee } = useEmployee()
  const { readOnly } = useContext(ReadOnlyContext)
  const { hasSensitiveAccess } = useClientInfo()
  const year = parseInt(ticketRight.validFrom.split('-')[0], 10)
  const category = employee.categories.find((it) => it.year === year)?.category
  const categoryName =
    ticketRight?.ticketType?.description?.split(' ')[3]?.trim() || category?.name || 'Ikke valgt'
  const lowIncome = familyMember.lowIncomes?.find((it) => it.year === year - 2)?.isRegistered

  // Ticket type ids that have the rule "Dagsrettighet"
  const COUPONS_TICKET_TYPE_IDS = [105, 106]

  const isPartner = familyMember.relation.code !== 'Barn'
  const hasCouponsRight = COUPONS_TICKET_TYPE_IDS.includes(ticketRight.ticketType.id)
  const [couponsStatus, setCouponsStatus] = useState({ totalCoupons: 0, remainingCoupons: 0 })

  const [ticketRightsResponse, generateTicketRights] = useGenerateTicketRights()
  const [lowIncomeResponse, setLowIncome] = useLowIncome(year - 2, familyMember, generateTicketRights)
  const [couponsLimitResponse, setCouponsLimit] = useApi(patchCouponsLimit, {
    successCallback: (_) => {
      fetchCouponsStatus({ id: familyMember.id, year: year.toString() })
    },
  })

  const [couponsStatusResponse, fetchCouponsStatus] = useApi(getCouponsStatus, {
    successCallback: (data) => {
      setCouponsStatus(data)
    },
  })

  useEffect(() => {
    if (hasSensitiveAccess && hasCouponsRight && couponsStatusResponse.status === FetchState.NOT_STARTED) {
      fetchCouponsStatus({ id: familyMember.id, year: year.toString() })
    }
  })

  const isErrorResponseStatus = (status: FetchState) => status === FetchState.ERROR

  const getErrorMessage: () => string | undefined = () => {
    if (isErrorResponseStatus(lowIncomeResponse.status) || isErrorResponseStatus(ticketRightsResponse.status))
      return 'Lav inntekt kunne ikke lagres'

    if (isErrorResponseStatus(couponsLimitResponse.status)) return 'Antall klipp kunne ikke bli oppdatert'

    if (isErrorResponseStatus(couponsStatusResponse.status)) return 'Antall klipp kunne ikke bli hentet'

    return undefined
  }

  const isError = getErrorMessage() !== undefined
  const isCouponStatusError = isErrorResponseStatus(couponsStatusResponse.status)

  const [isCouponsModalOpen, setCouponsModalOpen] = useState(false)
  const [newCoupons, setNewCoupons] = useState(0)
  const [errorMessage, setErrorMesssage] = useState('')

  const openCouponsModal = React.useCallback(() => setCouponsModalOpen(true), [setCouponsModalOpen])
  const updateLowIncome = React.useCallback(() => setLowIncome(!lowIncome), [lowIncome, setLowIncome])

  const onCouponsChange = React.useCallback(
    (value: string) => {
      if (isNaN(+value)) {
        return
      }
      setNewCoupons(+value)
      setErrorMesssage('')
    },
    [setNewCoupons, setErrorMesssage]
  )

  const onSubmitNewCoupons = React.useCallback(() => {
    if (newCoupons > 10) {
      setErrorMesssage('Verdien må være mindre enn eller lik 10')
      return
    }

    if (newCoupons < 1) {
      setErrorMesssage('Verdien må være høyere enn eller lik 1')
      return
    }

    setCouponsLimit({
      id: familyMember.id,
      year: year.toString(),
      body: { couponsLimit: couponsStatus.totalCoupons + newCoupons },
    })
    setCouponsModalOpen(false)
  }, [couponsStatus.totalCoupons, familyMember.id, newCoupons, setCouponsLimit, year])

  const lowIncomebadge = lowIncome ? (
    <Badge variant="neutral" style={{ float: 'left', marginRight: space.extraSmall }}>
      Lav inntekt
    </Badge>
  ) : undefined

  const isCouponsLoadingOrUndefined =
    couponsStatusResponse.status === FetchState.LOADING ? <Loader>Henter data</Loader> : undefined

  const couponBadge =
    hasCouponsRight && couponsStatusResponse.status === FetchState.SUCCESS ? (
      <Badge data-cy="coupons-badge" variant="neutral" style={{ float: 'left', marginRight: space.extraSmall }}>
        {couponsStatus.remainingCoupons} av {couponsStatus.totalCoupons} klipp igjen
      </Badge>
    ) : (
      isCouponsLoadingOrUndefined
    )

  const badges =
    lowIncomebadge || couponBadge ? (
      <>
        {lowIncomebadge}
        {couponBadge}
      </>
    ) : undefined

  const newCouponModal = (
    <AddNewCouponsModal
      errorMessage={errorMessage}
      setOpen={setCouponsModalOpen}
      isOpen={isCouponsModalOpen}
      isLoading={false}
      onSubmit={onSubmitNewCoupons}
      onChange={onCouponsChange}
    />
  )

  const couponActionChip =
    hasCouponsRight && !isCouponStatusError && !isCouponsLoadingOrUndefined ? (
      <>
        <CardActionChip data-cy="add-coupons-action-chip" onClick={openCouponsModal}>
          <AddIcon />
          Legg til klipp
        </CardActionChip>
        {newCouponModal}
      </>
    ) : undefined

  const lowIncomeActionChip = !lowIncome ? (
    <CardActionChip onClick={updateLowIncome}>
      <AddIcon />
      Lav inntekt
    </CardActionChip>
  ) : undefined

  const actionChips =
    !readOnly && isPartner ? (
      <>
        {lowIncomeActionChip}
        {couponActionChip}
      </>
    ) : undefined

  return (
    <>
      {isError && (
        <BannerAlertBox
          data-cy="family-ticket-right-card-error"
          className="spacing-bottom-large"
          variant="error"
          title="En feil oppstod"
          closable={false}
        >
          {getErrorMessage()}
        </BannerAlertBox>
      )}
      {draft && <ChangedTicketRightDraft activeTicketRight={ticketRight} draft={draft} />}
      <TicketRightCardBase
        ticketRight={ticketRight}
        categoryName={categoryName}
        badges={badges}
        canBeDeleted={false}
        familyMember={familyMember}
        actionChips={actionChips}
      >
        <RuleElement createdByRule={ticketRight?.createdByRule} />
      </TicketRightCardBase>
    </>
  )
}
